// The three rings of Architecture §2, as concrete layouts (P0.2).
//
//   command ring   main thread  → audio worklet    reject-and-count   (P0.3, P0.6)
//   signal ring    audio worklet → render worker    overwrite-oldest   (P0.3, P0.7)
//   audio ring     audio worklet → recorder / cue   (P1.6 SP-3, P0.3)
//
// Each has a version. Bump it whenever a record below changes shape: the two sides of a ring
// live in separately-built bundles that a browser can cache independently, so a stale worklet
// meeting a fresh main thread is a real deployment state. The version turns that from silent
// corruption into a loud failure at construction.

import { asByteOffset, type ByteOffset } from './branded';
import { computeRingLayout, type RingLayout } from './ring-layout';

const F32_BYTES = 4;

// ── Command ring: main thread → audio worklet ────────────────────────────────────────────

export const COMMAND_RING_VERSION = 1;

/**
 * One engine command. Field offsets within a slot.
 *
 * Note what is absent: the `ControlId` string and the `source`. Architecture §3 splits the bus
 * into two planes, and events that alter sound are "translated to engine commands, stamped
 * with AudioContext time, and written to the SAB command ring". The engine does not care that
 * a fader moved because of a mouse or a MIDI knob — that distinction lives on the control
 * plane. Numeric opcodes also keep the worklet from parsing strings, which would allocate.
 *
 * The opcode registry itself belongs to the engine, and arrives with commands worth sending
 * (P0.6's frequency/gain being the first two).
 */
export const COMMAND_RECORD = {
  /** f64 — AudioContext time. First, so it lands 8-aligned in every slot. */
  TIMESTAMP: 0,
  /** u16 — what to do. */
  OPCODE: 8,
  /** u16 — what to do it to (deck, channel, slot). */
  TARGET: 10,
  /** f32 — the value. */
  VALUE: 12,
} as const;

export const COMMAND_RECORD_BYTES = 16;

/**
 * 1024 slots ≈ 16 KB.
 *
 * The worklet drains this every render quantum — 2.7 ms at 48 kHz. Input peaks around 1 kHz
 * (a fast pointer drag), so a block carries ~3 commands. 1024 slots is roughly 340 blocks of
 * headroom: the ring only fills if the audio thread stalls for most of a second, by which
 * point rejected commands are the least of the problem. Cheap insurance at 16 KB.
 */
export const COMMAND_RING: RingLayout = computeRingLayout({
  name: 'command',
  version: COMMAND_RING_VERSION,
  capacitySlots: 1024,
  slotBytes: COMMAND_RECORD_BYTES,
});

// ── Signal ring: audio worklet → render worker ───────────────────────────────────────────

/**
 * 2 since P4.7, which appended two sources and a header field — the frame grew from 160 bytes to
 * 264, so a cached worklet meeting a fresh main thread would read every source at the wrong offset.
 * Exactly the deployment state the note at the top of this file describes, and the bump is what
 * turns it into a throw at construction instead of meters that read plausible nonsense.
 */
export const SIGNAL_RING_VERSION = 2;

/**
 * FR-26 requires the signal set "per deck and for the master". P4.7 adds the two **channel
 * strips**, and they are genuinely new sources rather than a second use of the deck's.
 *
 * ⚠️ **`DECK_A`/`DECK_B` and `STRIP_A`/`STRIP_B` are different measurement points and must not be
 * conflated.** A deck's block is published by `deck-processor` from the player's own output, before
 * the mixer exists in the chain. A strip's is taken inside `ChannelStrip`, *after* trim, EQ and
 * filter and *before* the fader — which is what FR-14 means by "pre-fader meter per channel". Both
 * are legitimate and they disagree the moment anyone touches an EQ knob. Writing the mixer's level
 * into the deck's source would silently redefine a number the waveform renderer and the browser
 * tests already read.
 *
 * **Appended after `MASTER: 2` so no existing index moves.** `deck.ts`'s standing rule: the two
 * sides of this ring ship in bundles that cache separately, so renumbering a source is the one edit
 * that can corrupt a frame without changing its size.
 */
export const SIGNAL_SOURCE = {
  DECK_A: 0,
  DECK_B: 1,
  MASTER: 2,
  STRIP_A: 3,
  STRIP_B: 4,
} as const;

export type SignalSourceIndex = (typeof SIGNAL_SOURCE)[keyof typeof SIGNAL_SOURCE];

export const SIGNAL_SOURCE_COUNT = 5;

/**
 * The FR-26 reactivity contract for one source, as f32 field offsets. This is the exact set
 * the PRD names — band energies, beat phase, tempo, energy, stereo balance, vocal estimate —
 * plus `RMS`, which drives meters (P0.7) and is a level rather than a musical signal.
 *
 * Only RMS is populated at P0.7. The rest arrive with the analysis pipeline in M2; they are
 * laid out now because FR-26 fixes the set, and because a layout is exactly the thing that
 * should be decided once rather than grown a field at a time.
 */
export const SIGNAL_SOURCE_RECORD = {
  SUB: 0,
  BASS: 4,
  MID: 8,
  HIGH: 12,
  ENERGY: 16,
  RMS: 20,
  BEAT_PHASE: 24,
  TEMPO: 28,
  STEREO_BALANCE: 32,
  VOCAL: 36,
  /**
   * f32 — the source's playhead, in **seconds** (P2.9).
   *
   * **Not one of FR-26's reactivity signals, and it is here anyway for a specific reason:
   * coherence.** A meter reading and a waveform playhead have to describe the *same block* — if the
   * UI reads a level from one buffer and a position from another, it can pair a peak from block N
   * with a position from block N+3, and the waveform's playhead will sit where the sound was 8 ms
   * ago. Inside one frame they are published and read atomically, so they cannot disagree.
   *
   * Seconds rather than frames, because this field is f32 and frames are not survivable in one: a
   * float32 holds integers exactly only to 2^24 (16.7M), and an 8-minute track at 48 kHz is 23M
   * frames — the playhead would start *skipping* two thirds of the way through every long track.
   * In seconds, f32's spacing at 480 s is ~30 µs, which is a thousandth of a pixel on the ribbon.
   * Frames are recovered by multiplying by the sample rate, which every consumer already knows.
   */
  PLAYHEAD_SECONDS: 40,
  /**
   * f32 — the loudest absolute sample in the block, as a linear magnitude (P4.7).
   *
   * **The reserved four bytes, spent.** The note on `SIGNAL_SOURCE_RECORD_BYTES` below has said
   * since P0.2 that the padding is "the next f32 field FR-26 grows"; this is that field, and it
   * lands at 44 because the fields above end exactly there. The record does not change size.
   *
   * **Peak and RMS both, because a limiter's whole subject is the difference between them.** RMS is
   * roughly what a level sounds like; peak is what actually has to fit. A signal can sit at a
   * comfortable −12 dB RMS and still clip, and the gap between the two numbers *is* the crest
   * factor the limiter is about to act on. One of them alone cannot say whether a meter reading is
   * a problem.
   */
  PEAK: 44,
} as const;

/**
 * 48, and since P4.7 the fields fill it exactly — no padding left.
 *
 * `computeRingLayout` requires a slot to be a multiple of 8 so the frame's f64 timestamp lands
 * aligned, and a 44-byte record makes the frame 148. The guard refused the build, which is P0.2's
 * byte-math check earning its place four prompts later. Those four spare bytes were reserved for
 * "the next f32 field FR-26 grows" and `PEAK` is now in them.
 *
 * ⚠️ **The next field to arrive costs 8 bytes per source, not 4**, because the record has to stay
 * a multiple of 8 for the frame to stay one. That is a real decision rather than an increment —
 * 56 bytes × 5 sources is 40 bytes a frame, 2.5 KB across the ring — so weigh it rather than
 * reaching for it.
 */
export const SIGNAL_SOURCE_RECORD_BYTES = 48;

export const SIGNAL_FRAME = {
  /** f64 — AudioContext time of the block this frame describes. */
  TIMESTAMP: 0,
  /** u32 — the producing block's sequence, for detecting skipped frames. */
  SEQUENCE: 8,
  /** u32 — one bit per source: an onset occurred in this block (FR-26's "onset events"). */
  ONSET_FLAGS: 12,
  /**
   * f32 — what the master limiter took off this block, as a **linear gain**. 1 is untouched (P4.7).
   *
   * ⚠️ **In the frame header rather than in a source record, and the placement is the claim that
   * there is exactly one of it.** There is one limiter, on one master bus. A per-source field would
   * be permanently zero for four of five sources and would invite a second limiter to be read into
   * the design later — the drift `ADR-LOG.md` exists to catch.
   *
   * **It has to live in the frame at all** because of P4.7's rule: the reduction and the master
   * peak are one reading. A meter saying "+6 dB over" beside a reduction of 1.0 is not a surprising
   * pair of numbers, it is two numbers from different blocks, and nobody could reproduce it.
   *
   * ⚠️ **A gain, so the *worst* value across an interval is the minimum, not the maximum.** Every
   * other quantity here accumulates by `max`. Taking a max of this would report "no reduction" on
   * exactly the block where the limiter worked hardest — see `readFramePeaks`.
   */
  GAIN_REDUCTION: 16,
  /**
   * SIGNAL_SOURCE_COUNT × SIGNAL_SOURCE_RECORD_BYTES follow.
   *
   * 24 rather than 20: the frame has to be a multiple of 8 and 20 + 5 × 48 is 260, so byte 20 is
   * four bytes of header padding and the next u32/f32 the frame header grows goes there for free.
   * Records are all f32, so they need only 4-byte alignment and the hole costs nothing but itself.
   */
  SOURCES: 24,
} as const;

export const SIGNAL_FRAME_BYTES =
  SIGNAL_FRAME.SOURCES + SIGNAL_SOURCE_COUNT * SIGNAL_SOURCE_RECORD_BYTES;

/**
 * 64 slots ≈ 16.7 KB (10.2 KB before P4.7 added two sources and a gain-reduction field; 8.5 KB
 * before P2.9 widened the source record by a playhead).
 *
 * The producer writes every 2.7 ms; the consumer reads once a frame, ~16.7 ms at 60 FPS — so
 * roughly 6 slots pile up per frame and the reader is always behind by design. That is what
 * overwrite-oldest is for: the renderer wants the newest signals, not a backlog. 64 slots is
 * ~10 frames of slack, which absorbs a stuttering renderer without ever stalling the audio
 * thread. Audio never waits for visuals — Principle 1, and R-1's whole mitigation.
 */
export const SIGNAL_RING: RingLayout = computeRingLayout({
  name: 'signal',
  version: SIGNAL_RING_VERSION,
  capacitySlots: 64,
  slotBytes: SIGNAL_FRAME_BYTES,
});

/** Where a source's block begins, relative to the start of its frame. */
export function signalSourceByteOffset(source: SignalSourceIndex): ByteOffset {
  return asByteOffset(SIGNAL_FRAME.SOURCES + source * SIGNAL_SOURCE_RECORD_BYTES);
}

// ── Audio ring: audio worklet → recorder worker / cue bridge ─────────────────────────────

export const AUDIO_RING_VERSION = 1;

/** Web Audio's render quantum. Not a tunable — the spec fixes it at 128 frames. */
export const RENDER_QUANTUM_FRAMES = 128;

export const AUDIO_RING_CHANNELS = 2;

/**
 * One block of stereo audio: 1 KB.
 *
 * Planar, not interleaved — `[128 frames L][128 frames R]`. Web Audio hands the worklet one
 * Float32Array per channel already, so planar is a straight copy per channel while
 * interleaving would mean a per-sample loop on the audio thread for no one's benefit.
 */
export const AUDIO_BLOCK_BYTES = RENDER_QUANTUM_FRAMES * AUDIO_RING_CHANNELS * F32_BYTES;

/**
 * 128 blocks ≈ 128 KB ≈ 341 ms at 48 kHz.
 *
 * Capacity is not latency — the fill level is — so this only sets how much jitter the ring can
 * absorb before it drops. A provisional default: **SP-3 (P1.7) rules on the real number**,
 * having measured drift across a 60-minute soak and mismatched device sample rates. Treat it
 * as a placeholder with a reason, not a decision.
 */
export const AUDIO_RING: RingLayout = computeRingLayout({
  name: 'audio',
  version: AUDIO_RING_VERSION,
  capacitySlots: 128,
  slotBytes: AUDIO_BLOCK_BYTES,
});

/** Where a channel's samples begin, relative to the start of its block. */
export function audioChannelByteOffset(channel: number): ByteOffset {
  return asByteOffset(channel * RENDER_QUANTUM_FRAMES * F32_BYTES);
}
